// Parser for `@if` + `elseif/else` control-flow chains in .luis source.
// Example: `@if(a===b){...}elseif(a===c){...}else{...}`.
import type { IfBranch, IfChainParseResult } from './types'
import { skipWhitespace } from './scanner-utils'
import { matchIfTokenAt, parseConditionalBranch, parseElseBranch } from './if-parser-branches'

// Locate next `@if(` token from the current scanner cursor.
export function findNextIfStart(source: string, fromIndex: number): number {
  for (let index = fromIndex; index < source.length; index += 1) {
    const matched = matchIfTokenAt(source, index)
    // Continue scan until scanner finds an `@if` token boundary.
    if (!matched) {
      continue
    } else {
      // Return first matched `@if` token position.
      return index
    }
  }

  // Return -1 when no `@if` token is found.
  return -1
}

// Parse one `@if ... elseif ... else ...` chain and return boundaries.
export function parseIfChain(source: string, startIndex: number): IfChainParseResult | null {
  const firstBranch = parseConditionalBranch(source, startIndex, 'if')
  // Reject parsing when starting token is not a valid `@if` branch.
  if (!firstBranch) {
    return null
  } else {
    // Continue parsing optional elseif/else branches.
  }

  const branches: IfBranch[] = [firstBranch.branch]
  let cursor = firstBranch.endIndex

  // Parse all subsequent `elseif` and optional `else` branches.
  while (cursor < source.length) {
    cursor = skipWhitespace(source, cursor)
    const elseifBranch = parseConditionalBranch(source, cursor, 'elseif')
    // Keep consuming `elseif` branches while they are present.
    if (elseifBranch) {
      branches.push(elseifBranch.branch)
      cursor = elseifBranch.endIndex
      continue
    } else {
      // Continue optional `else` parsing.
    }

    const elseBranch = parseElseBranch(source, cursor)
    // Parse optional trailing `else` branch and finish chain.
    if (elseBranch) {
      branches.push(elseBranch.branch)
      cursor = elseBranch.endIndex
    } else {
      // Keep chain without `else` branch.
    }

    break
  }

  // Return parsed branch list with end cursor.
  return {
    branches,
    endIndex: cursor,
  }
}
